import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { UserCircleIcon, BellIcon, ShieldCheckIcon, PaintBrushIcon, KeyIcon } from '@heroicons/react/24/outline';
import { Card, Button, Input, Select } from '../../components/Common';

const Settings = () => {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('profile');
  const [saving, setSaving] = useState(false);

  const [profile, setProfile] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [notifications, setNotifications] = useState({
    emailNotifications: true,
    pushNotifications: true,
    weeklyDigest: false,
    newContent: true,
    newUsers: true,
  });

  const [security, setSecurity] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    twoFactorEnabled: false,
  });

  const handleSave = async () => {
    setSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setSaving(false);
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: UserCircleIcon },
    { id: 'notifications', label: 'Notifications', icon: BellIcon },
    { id: 'security', label: 'Security', icon: ShieldCheckIcon },
    { id: 'appearance', label: 'Appearance', icon: PaintBrushIcon },
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Settings</h1>
        <p className="text-slate-500">Manage your account and preferences</p>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar */}
        <div className="lg:w-64 flex-shrink-0">
          <Card noPadding>
            <nav className="p-2">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-colors ${
                    activeTab === tab.id
                      ? 'bg-indigo-50 text-indigo-600'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              ))}
            </nav>
          </Card>
        </div>

        {/* Content */}
        <div className="flex-1">
          {activeTab === 'profile' && (
            <Card title="Profile Settings" subtitle="Update your personal information" icon={UserCircleIcon}>
              <div className="space-y-6">
                <div className="flex items-center gap-6">
                  <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {(profile.firstName?.charAt(0) || 'U').toUpperCase()}
                  </div>
                  <div>
                    <Button variant="secondary" size="sm">Change Photo</Button>
                    <p className="text-sm text-slate-500 mt-2">JPG, PNG or GIF. Max 2MB.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="First Name" value={profile.firstName} onChange={(e) => setProfile(prev => ({ ...prev, firstName: e.target.value }))} placeholder="John" />
                  <Input label="Last Name" value={profile.lastName} onChange={(e) => setProfile(prev => ({ ...prev, lastName: e.target.value }))} placeholder="Doe" />
                </div>
                <Input label="Email" type="email" value={profile.email} onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))} placeholder="john@example.com" />
                <Input label="Phone" value={profile.phone} onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))} placeholder="+1234567890" />

                <div className="flex justify-end">
                  <Button onClick={handleSave} loading={saving}>Save Changes</Button>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'notifications' && (
            <Card title="Notification Preferences" subtitle="Choose what notifications you receive" icon={BellIcon}>
              <div className="space-y-6">
                {[
                  { key: 'emailNotifications', label: 'Email Notifications', desc: 'Receive notifications via email' },
                  { key: 'pushNotifications', label: 'Push Notifications', desc: 'Receive push notifications in browser' },
                  { key: 'weeklyDigest', label: 'Weekly Digest', desc: 'Get a weekly summary of activity' },
                  { key: 'newContent', label: 'New Content Alerts', desc: 'Get notified when new content is published' },
                  { key: 'newUsers', label: 'New User Alerts', desc: 'Get notified when new users register' },
                ].map((item) => (
                  <div key={item.key} className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
                    <div>
                      <p className="font-medium text-slate-800">{item.label}</p>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                    <button
                      onClick={() => setNotifications(prev => ({ ...prev, [item.key]: !prev[item.key] }))}
                      className={`switch ${notifications[item.key] ? 'active' : ''}`}
                    />
                  </div>
                ))}

                <div className="flex justify-end">
                  <Button onClick={handleSave} loading={saving}>Save Preferences</Button>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'security' && (
            <Card title="Security Settings" subtitle="Manage your password and security options" icon={ShieldCheckIcon}>
              <div className="space-y-6">
                <div className="p-4 bg-slate-50 rounded-xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                        <KeyIcon className="w-5 h-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">Two-Factor Authentication</p>
                        <p className="text-sm text-slate-500">Add an extra layer of security</p>
                      </div>
                    </div>
                    <button
                      onClick={() => setSecurity(prev => ({ ...prev, twoFactorEnabled: !prev.twoFactorEnabled }))}
                      className={`switch ${security.twoFactorEnabled ? 'active' : ''}`}
                    />
                  </div>
                </div>

                <div className="border-t border-slate-200 pt-6">
                  <h4 className="font-medium text-slate-800 mb-4">Change Password</h4>
                  <div className="space-y-4">
                    <Input label="Current Password" type="password" value={security.currentPassword} onChange={(e) => setSecurity(prev => ({ ...prev, currentPassword: e.target.value }))} placeholder="••••••••" />
                    <Input label="New Password" type="password" value={security.newPassword} onChange={(e) => setSecurity(prev => ({ ...prev, newPassword: e.target.value }))} placeholder="••••••••" />
                    <Input label="Confirm New Password" type="password" value={security.confirmPassword} onChange={(e) => setSecurity(prev => ({ ...prev, confirmPassword: e.target.value }))} placeholder="••••••••" />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={handleSave} loading={saving}>Update Password</Button>
                </div>
              </div>
            </Card>
          )}

          {activeTab === 'appearance' && (
            <Card title="Appearance" subtitle="Customize the look and feel" icon={PaintBrushIcon}>
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-3">Theme</label>
                  <div className="grid grid-cols-3 gap-4">
                    {['Light', 'Dark', 'System'].map((theme) => (
                      <button
                        key={theme}
                        className={`p-4 rounded-xl border-2 transition-colors ${
                          theme === 'Light' ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'
                        }`}
                      >
                        <div className={`w-full h-16 rounded-lg mb-2 ${theme === 'Dark' ? 'bg-slate-800' : 'bg-white border border-slate-200'}`}></div>
                        <p className="font-medium text-slate-800">{theme}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <Select label="Language" options={[{ value: 'en', label: 'English' }, { value: 'es', label: 'Spanish' }, { value: 'fr', label: 'French' }]} />
                <Select label="Timezone" options={[{ value: 'utc', label: 'UTC' }, { value: 'est', label: 'Eastern Time' }, { value: 'pst', label: 'Pacific Time' }]} />

                <div className="flex justify-end">
                  <Button onClick={handleSave} loading={saving}>Save Preferences</Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
